import {
  PaymentHistoryItem,
  PaymentMethod,
  PaymentStatus,
  SubscriptionInfo,
  SubscriptionPlan,
  SubscriptionPlanType,
  SubscriptionStatus,
} from 'types'

// Prices and seat caps are the purchase-flow fallback. The selector prefers Stripe amounts from public config when that request succeeds.
// Keep in sync with backend config.familyMaxSeats (default 10) and the monthly Stripe prices.
export const FAMILY_SUBSCRIPTION_MAX_SEATS = 10
export const FAMILY_SUBSCRIPTION_PRICE = 25
export const INDIVIDUAL_SUBSCRIPTION_PRICE = 9

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    type: 'caregiver_monthly',
    label: 'Individual Subscription',
    price: INDIVIDUAL_SUBSCRIPTION_PRICE,
    period: 'month',
  },
  {
    type: 'caregiver_family',
    label: 'Family Subscription',
    price: FAMILY_SUBSCRIPTION_PRICE,
    period: 'month',
    maxSeats: FAMILY_SUBSCRIPTION_MAX_SEATS,
  },
]

/** Existing subscriptions may still be on these plans. They are not offered for a new purchase. */
const LEGACY_SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    type: 'caregiver_3_month',
    label: '3-Month Subscription',
    price: 15,
    period: '3 months',
  },
  {
    type: 'caregiver_annual',
    label: 'Annual Subscription',
    price: 45,
    period: 'year',
  },
]

// Mock Data for Development (without backend)
export const MOCK_PAYMENT_METHOD: PaymentMethod = {
  id: 'pm_mock_123',
  brand: 'visa',
  last4: '4242',
  expMonth: 12,
  expYear: 2027,
}

export const MOCK_SUBSCRIPTION_INFO: SubscriptionInfo = {
  id: 'sub_mock_456',
  planType: 'caregiver_annual',
  status: SubscriptionStatus.Active,
  renewalDate: '2027-01-28',
  paymentMethod: MOCK_PAYMENT_METHOD,
}

export const MOCK_PAYMENT_HISTORY: PaymentHistoryItem[] = [
  {
    id: 'pi_mock_001',
    date: '2026-01-28',
    planType: 'caregiver_annual',
    status: PaymentStatus.Paid,
    amount: 45,
  },
  {
    id: 'pi_mock_002',
    date: '2025-01-28',
    planType: 'caregiver_annual',
    status: PaymentStatus.Paid,
    amount: 45,
  },
  {
    id: 'pi_mock_003',
    date: '2024-01-28',
    planType: 'caregiver_3_month',
    status: PaymentStatus.Paid,
    amount: 15,
  },
]

// Helpers
export const getPlanByType = (type: SubscriptionPlanType): SubscriptionPlan | undefined => {
  return (
    SUBSCRIPTION_PLANS.find((plan) => plan.type === type) ??
    LEGACY_SUBSCRIPTION_PLANS.find((plan) => plan.type === type)
  )
}

/**
 * Long labels for plans that are not in `SUBSCRIPTION_PLANS`. The trainee plan is deliberately kept
 * out of that list because it drives the caregiver plan selector, but its payments still show up in
 * payment history tables and need a readable name.
 */
const EXTRA_PLAN_LABELS: Partial<Record<SubscriptionPlanType, string>> = {
  trainee_monthly: 'Individual Trainee Subscription',
  caregiver_family_funded: 'Partner-Funded Family Subscription',
}

/** Full plan name for tables — falls back to the raw value so an unknown plan is still readable. */
export const getPlanLabel = (type: SubscriptionPlanType): string => {
  return getPlanByType(type)?.label ?? EXTRA_PLAN_LABELS[type] ?? type
}

/** Short display name used on My Subscription page header (per spec: "Annual Plan / Promo Code / Family Plan"). */
const PLAN_DISPLAY_NAMES: Record<SubscriptionPlanType, string> = {
  caregiver_monthly: 'Individual Subscription',
  caregiver_3_month: '3-Month Plan',
  caregiver_annual: 'Annual Plan',
  caregiver_family: 'Family Plan',
  caregiver_family_funded: 'Partner-Funded Family Plan',
  trainee_monthly: 'Individual Trainee Plan',
}

export const getPlanDisplayName = (type: SubscriptionPlanType): string => {
  return PLAN_DISPLAY_NAMES[type] ?? type
}

export const formatCardDisplay = (paymentMethod: PaymentMethod): string => {
  const brandCapitalized =
    paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)
  return `${brandCapitalized} **** ${paymentMethod.last4}`
}

export const formatExpiryDate = (paymentMethod: PaymentMethod): string => {
  const month = paymentMethod.expMonth.toString().padStart(2, '0')
  const year = paymentMethod.expYear.toString().slice(-2)
  return `${month}/${year}`
}
